import React from 'react';
import { toast } from 'sonner';
import { ExternalLink, Radar } from 'lucide-react';
import { Panel, Empty, StatusBadge } from '../../components/Primitives';
import { useFetch } from '../../lib/hooks';
import { api, errMsg, fmtUsd, fmtDate, shortHash } from '../../lib/api';

export default function AdminDeposits() {
  const { data: rows, reload } = useFetch('/admin/deposits', [], 20000);
  const { data: users } = useFetch('/admin/users');
  const [mf, setMf] = React.useState({ user_id: '', amount: '' });
  const act = async (id, action) => {
    try { await api.post(`/admin/deposits/${id}/${action}`); toast.success(`Deposit ${action === 'credit' ? 'credited' : 'rejected'}`); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const [assignTo, setAssignTo] = React.useState({});
  const [scanning, setScanning] = React.useState(false);
  const scan = async () => {
    setScanning(true);
    try { const { data } = await api.post('/admin/deposits/scan'); toast.success(`Chain scanned — ${data.transfers_found} new transfer(s)`); reload(); }
    catch (err) { toast.error(errMsg(err)); } finally { setScanning(false); }
  };
  const assign = async (d) => {
    const uid = assignTo[d.id] || d.claimed_by;
    if (!uid) return toast.error('Select a user to assign');
    try { await api.post(`/admin/deposits/${d.id}/assign`, { user_id: uid }); toast.success('Deposit assigned'); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const clientUsers = (users || []).filter((u) => u.role === 'user');
  const manual = async (e) => {
    e.preventDefault();
    if (!mf.user_id || !(Number(mf.amount) > 0)) return toast.error('Select user and amount');
    try { await api.post('/admin/deposits/manual', mf); toast.success('Admin-recorded deposit credited'); setMf({ user_id: '', amount: '' }); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="space-y-6" data-testid="admin-deposits">
      <Panel title="Record Manual Deposit (Dev)">
        <form onSubmit={manual} className="grid sm:grid-cols-3 gap-3">
          <select value={mf.user_id} onChange={(e) => setMf({ ...mf, user_id: e.target.value })} className="h-11 px-3 rounded-lg bg-vj-charcoal border border-border text-sm text-foreground" data-testid="manual-dep-user">
            <option value="">Select user…</option>
            {(users || []).filter((u) => u.role === 'user').map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
          </select>
          <input type="number" step="0.01" value={mf.amount} onChange={(e) => setMf({ ...mf, amount: e.target.value })} placeholder="Amount (USDT)" className="h-11 px-3 rounded-lg bg-vj-charcoal border border-border font-mono text-sm text-foreground" data-testid="manual-dep-amount" />
          <button className="h-11 rounded-lg bg-vj-gold/90 text-vj-obsidian font-semibold text-sm" data-testid="manual-dep-submit">Credit (Admin-Recorded)</button>
        </form>
        <p className="text-[11px] text-vj-grey mt-3">Dev-only: records an auditable admin-recorded credit, clearly labelled and distinct from on-chain verified deposits. Use for demos until real on-chain deposits are available.</p>
      </Panel>
      <Panel title="Deposit Verification & Crediting" action={
        <button onClick={scan} disabled={scanning} className="h-8 px-3 rounded-lg border border-vj-neon/40 text-vj-neon text-xs inline-flex items-center gap-1.5 hover:bg-vj-neon/10 disabled:opacity-60" data-testid="deposit-scan-btn">
          <Radar size={13} /> {scanning ? 'Scanning…' : 'Scan chain now'}
        </button>}>
        {rows && rows.length ? (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-vj-grey border-b border-border"><th className="py-2 font-medium">User</th><th className="font-medium">From</th><th className="font-medium">Amount</th><th className="font-medium">Tx</th><th className="font-medium">Conf</th><th className="font-medium">Status</th><th className="font-medium">Date</th><th className="font-medium">Actions</th></tr></thead>
            <tbody>{rows.map((d) => (
              <tr key={d.id} className="border-b border-border last:border-0">
                <td className="py-2.5 text-xs">
                  {d.user_id ? d.user_email : (
                    <div className="space-y-1" data-testid={`unattributed-${d.id}`}>
                      <span className="text-vj-gold">Unattributed</span>
                      {d.claimed_by_email && <div className="text-[11px] text-vj-grey">Claimed by {d.claimed_by_email}</div>}
                      <div className="flex gap-1">
                        <select value={assignTo[d.id] || d.claimed_by || ''} onChange={(e) => setAssignTo({ ...assignTo, [d.id]: e.target.value })} className="h-7 px-1.5 rounded-md bg-vj-charcoal border border-border text-[11px] text-foreground max-w-[140px]" data-testid={`assign-user-${d.id}`}>
                          <option value="">Assign to…</option>
                          {clientUsers.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
                        </select>
                        <button onClick={() => assign(d)} className="h-7 px-2 rounded-md bg-vj-gold/15 text-vj-gold text-[11px]" data-testid={`assign-${d.id}`}>Assign</button>
                      </div>
                    </div>
                  )}
                </td>
                <td className="font-mono text-[11px] text-vj-grey">{d.from_address?.startsWith("0x") ? shortHash(d.from_address) : d.from_address}</td>
                <td className="font-mono">{fmtUsd(d.amount)}</td>
                <td><a href={`https://bscscan.com/tx/${d.tx_hash}`} target="_blank" rel="noreferrer" className="font-mono text-xs text-vj-neon inline-flex items-center gap-1">{shortHash(d.tx_hash)}<ExternalLink size={11} /></a></td>
                <td className="font-mono text-xs">{d.confirmations >= d.required_confirmations ? `${d.required_confirmations}+` : d.confirmations}/{d.required_confirmations}</td>
                <td><StatusBadge status={d.status} /></td>
                <td className="text-xs text-vj-grey">{fmtDate(d.created_at)}</td>
                <td>
                  {['blockchain_verified', 'pending_review'].includes(d.status) && d.user_id && (
                    <div className="flex gap-1.5">
                      <button onClick={() => act(d.id, 'credit')} className="h-7 px-2.5 rounded-md bg-vj-neon/15 text-vj-neon text-xs" data-testid={`credit-${d.id}`}>Credit</button>
                      <button onClick={() => act(d.id, 'reject')} className="h-7 px-2.5 rounded-md bg-vj-red/15 text-vj-red text-xs" data-testid={`reject-${d.id}`}>Reject</button>
                    </div>
                  )}
                  {d.status === 'confirming' && <span className="text-xs text-vj-grey">Awaiting confirmations</span>}
                  {d.status === 'credited' && <span className="text-xs text-vj-neon">Credited</span>}
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        ) : <Empty>No deposits submitted yet.</Empty>}
      </Panel>
    </div>
  );
}
